import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { IOAuthProviderConfig } from "../../@types/OAuth";
import { CircleTick } from "../../assets/icons/CircleTick";
import { CircleX } from "../../assets/icons/CircleX";
import { colors } from "../../styles/colors";
import { preLoginLogo } from "../../styles/preLoginBranding";
import {
  Card,
  IconContainer,
  InfoText,
  Logo,
  MainContainer,
  Message,
  Spinner,
  Title,
} from "./OAuthCallbackPage.styles";

type Status = "loading" | "success" | "error";

interface IPageState {
  status: Status;
  title: string;
  message: string;
}

interface IApiError {
  code: string;
  message: string;
}

interface IErrorResponse {
  errors?: IApiError[];
}

const ERROR_MESSAGES: Record<string, { title: string; message: string }> = {
  CodeExpired: {
    title: "Session Expired",
    message:
      "Your authorization session has expired. Please close this window and try connecting again from the app.",
  },
  InvalidCode: {
    title: "Invalid Session",
    message:
      "The authorization session is invalid or has already been used. Please close this window and try connecting again from the app.",
  },
  InvalidArgumentException: {
    title: "Already Connected",
    message:
      "This authorization has already been completed. You can close this window.",
  },
};

interface IOAuthCallbackPageProps {
  config: IOAuthProviderConfig;
}

function getErrorState(
  error: unknown,
  providerName: string
): Omit<IPageState, "status"> {
  const errorData = (error as { data?: IErrorResponse })?.data;
  const firstError = errorData?.errors?.[0];

  if (firstError?.code && ERROR_MESSAGES[firstError.code]) {
    return ERROR_MESSAGES[firstError.code];
  }

  const errorMessage =
    firstError?.message ||
    (error as { data?: { message?: string } })?.data?.message ||
    (error as { message?: string })?.message ||
    `An error occurred while connecting your ${providerName} account. Please try again.`;

  return { title: "Connection Failed", message: errorMessage };
}

export function OAuthCallbackPage({ config }: IOAuthCallbackPageProps) {
  const [state, setState] = useState<IPageState>({
    status: "loading",
    title: "Connecting...",
    message: `Please wait while we connect your ${config.providerName} account...`,
  });
  const [searchParams] = useSearchParams();

  const { providerName, completeAuthorization } = config;

  useEffect(() => {
    async function handleAuthorization(): Promise<void> {
      const code = searchParams.get("code");
      const state = searchParams.get("state");

      if (!code || !state) {
        setState({
          status: "error",
          title: "Connection Failed",
          message: "Invalid callback. Missing authorization code or state.",
        });
        return;
      }

      try {
        const response = await completeAuthorization({
          authorizationCode: code,
          state,
        });

        if (response.success) {
          setState({
            status: "success",
            title: "Connected!",
            message:
              response.message ||
              `${providerName} account connected successfully!`,
          });
        } else {
          setState({
            status: "error",
            title: "Connection Failed",
            message:
              response.message || `Failed to connect ${providerName} account.`,
          });
        }
      } catch (error) {
        const errorState = getErrorState(error, providerName);
        setState({ status: "error", ...errorState });
      }
    }

    handleAuthorization();
  }, [searchParams, completeAuthorization, providerName]);

  function renderIcon(): React.ReactNode {
    if (state.status === "loading") {
      return <Spinner />;
    }
    if (state.status === "success") {
      return <CircleTick width={44} height={44} color={colors.success} />;
    }
    return <CircleX width={44} height={44} color={colors.error} />;
  }

  return (
    <MainContainer>
      <Card>
        <Logo src={preLoginLogo.src} alt={preLoginLogo.alt} />
        <IconContainer status={state.status}>{renderIcon()}</IconContainer>
        <Title>{state.title}</Title>
        <Message>{state.message}</Message>
        {state.status !== "loading" && (
          <InfoText>
            {state.status === "success"
              ? "You can now close this window and return to the app."
              : "Please close this window and try again from the app."}
          </InfoText>
        )}
      </Card>
    </MainContainer>
  );
}

export type { IOAuthProviderConfig };
